import { useMemo } from 'react';
import './UserAvatar.css';

export type UserAvatarSize = number;

function cx(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(' ');
}

interface UserAvatarProps {
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  size?: UserAvatarSize;
  className?: string;
}

function getInitialsFromName(name: string): string {
  const cleaned = name
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^\p{L}\p{N}\s'-]/gu, '');

  if (!cleaned) return '';

  const parts = cleaned.split(' ').filter(Boolean);
  if (parts.length === 1) {
    const one = parts[0] ?? '';
    return one.slice(0, 2).toUpperCase();
  }

  const first = parts[0]?.charAt(0) ?? '';
  const last = parts[parts.length - 1]?.charAt(0) ?? '';
  return `${first}${last}`.toUpperCase();
}

function getInitials({
  name,
  email,
}: {
  name?: string | null;
  email?: string | null;
}): string {
  if (name && typeof name === 'string') {
    const initials = getInitialsFromName(name);
    if (initials) return initials;
  }

  if (email && typeof email === 'string') {
    const firstChar = email.trim().charAt(0);
    if (firstChar) return firstChar.toUpperCase();
  }

  return 'U';
}

export function UserAvatar({ name, email, avatarUrl, size = 40, className }: UserAvatarProps) {
  const initials = useMemo(() => getInitials({ name, email }), [name, email]);
  const safeAvatarUrl = typeof avatarUrl === 'string' && avatarUrl.trim() ? avatarUrl.trim() : '';
  const fontSize = Math.max(12, Math.round(size * 0.42));

  return (
    <div
      className={cx('user-avatar', className)}
      style={{ width: size, height: size }}
      aria-label={name || email || 'Usuario'}
      role="img"
    >
      {safeAvatarUrl ? (
        <img className="user-avatar__img" src={safeAvatarUrl} alt={name || email || 'Usuario'} />
      ) : (
        <span className="user-avatar__initials" style={{ fontSize }} aria-hidden="true">
          {initials}
        </span>
      )}
    </div>
  );
}

